import { useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import { FilterOptionList } from './FilterOptionList';
import { FILTER_ALL_VALUE, filterValueLabel, type FilterDefinition } from './filter-types';

interface FilterChipProps {
  definition: FilterDefinition;
  /**
   * Called after the chip clears itself. Removing a chip unmounts the element
   * that had focus, so the bar uses this to put focus somewhere sensible
   * instead of letting it fall back to the document.
   */
  onRemoved?: () => void;
  className?: string;
}

/**
 * One applied filter, shown as `Label · Value` with its own controls: the body
 * opens a searchable list to change the value, the trailing × removes it.
 *
 * Splitting change and remove is the point. A single dropdown makes "stop
 * filtering by repo" a hunt for an "All repos" row buried in a list of repos,
 * while a bare × makes changing a value a delete-then-re-add round trip.
 */
export function FilterChip({ definition, onRemoved, className }: FilterChipProps) {
  const [open, setOpen] = useState(false);
  const Icon = definition.icon;
  const valueLabel = filterValueLabel(definition);

  const remove = () => {
    definition.onValueChange(FILTER_ALL_VALUE);
    onRemoved?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div
        className={cn(
          'inline-flex h-7 shrink-0 items-center rounded-md border border-ring/30 bg-ring/[0.07] text-xs',
          'animate-in fade-in-0 zoom-in-95 duration-150',
          open && 'border-ring/50 bg-ring/[0.12]',
          className,
        )}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label={`${definition.label}: ${valueLabel}`}
            data-testid={`filter-chip-${definition.id}`}
            onKeyDown={(event) => {
              // Focused chip + Backspace removes it, the way a token behaves in
              // a search field. Delete is accepted for the same reflex.
              if (!open && (event.key === 'Backspace' || event.key === 'Delete')) {
                event.preventDefault();
                remove();
              }
            }}
            className={cn(
              'flex h-full min-w-0 items-center gap-1 rounded-l-md pl-2 pr-1 transition-colors',
              'hover:bg-ring/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
            )}
          >
            {Icon && <Icon className="size-3 shrink-0 text-muted-foreground" aria-hidden="true" />}
            <span className="shrink-0 text-muted-foreground">{definition.label}</span>
            <span className="max-w-40 truncate font-medium text-foreground">{valueLabel}</span>
            <ChevronDown className="size-3 shrink-0 opacity-50" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <button
          type="button"
          aria-label={`Remove ${definition.label} filter`}
          data-testid={`filter-chip-remove-${definition.id}`}
          onClick={remove}
          className={cn(
            'flex h-full items-center rounded-r-md border-l border-ring/20 px-1.5 text-muted-foreground transition-colors',
            'hover:bg-destructive/10 hover:text-destructive',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
          )}
        >
          <X className="size-3" aria-hidden="true" />
        </button>
      </div>
      <PopoverContent align="start" className="w-56 p-0">
        <FilterOptionList
          definition={definition}
          onSelect={(value) => {
            definition.onValueChange(value);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
